// AnomalieModal.vue
<script setup>
import { ref, computed, inject } from 'vue'
import { useStore } from 'vuex'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import ProgressiveImageUploader from '@/views/appLivraisons/ProgressiveImageUploader.vue'
import axios from '@/api/axios'
import dataService from '@/services/dataService'

// Props
const props = defineProps({
  delivery: {
    type: Object,
    required: true
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// Store
const store = useStore()
const isOnline = computed(() => store.getters['sync/isOnline'])

// Bus d'événements
const eventBus = inject('eventBus')

// Références pour nos uploaders
const anomalieUploader = ref(null)

// État local pour les états des images
const anomalieUploaded = ref(false)

// État local pour les IDs d'images côté serveur
const anomalieServerId = ref(null)

// État local pour les données des images
const anomalieImageData = ref(null)

// État local
const clientCalled = ref(null)
const operationsCalled = ref(null)
const anomalyReason = ref(null)

// Liste des raisons d'anomalie
const anomalyReasons = [
  'Client Absent',
  'Adresse Introuvable',
  'Refus du client'
]

// Computed
const isFormValid = computed(() => {
  return (
    clientCalled.value === true &&
    operationsCalled.value === true &&
    anomalieUploaded.value &&
    anomalyReason.value !== null
  )
})

// Vérifier si tous les uploads sont terminés (Online mode)
const allUploadsComplete = computed(() => {
  if (!isOnline.value) return true; // En mode hors ligne, on considère tout comme complet
  
  return anomalieServerId.value !== null;
})

// Méthodes pour gérer les uploads réussis (prise de photo)
const handleAnomalieSuccess = (imageId, imageData) => {
  anomalieUploaded.value = true
  anomalieImageData.value = imageData
}

// Méthodes pour gérer les uploads complets (images envoyées au serveur)
const handleAnomalieUploadComplete = (serverId, imageData) => {
  anomalieServerId.value = serverId
  // Mettre à jour les données avec l'ID du serveur
  if (anomalieImageData.value) {
    anomalieImageData.value.serverImageId = serverId
  }
}

// Méthode pour gérer les erreurs d'upload
const handleUploadError = (error) => {
  console.error('Upload error:', error)
}

const callClient = () => {
  if (props.delivery.deliveryLocation?.telephone) {
    const cleanPhone = props.delivery.deliveryLocation.telephone.replace(/\s+/g, '')
    window.location.href = `tel:${cleanPhone}`
  }
}

const callSupport = () => {
  window.location.href = 'tel:+33187164750'
}

// Méthode de confirmation de la livraison
const handleConfirm = async () => {
  if (!isFormValid.value) return false

  try {
    // Si nous sommes en ligne et que les uploaders ont déjà envoyé les images
    // nous collectons les IDs serveur directement
    if (isOnline.value && allUploadsComplete.value) {
      console.log('Toutes les images déjà uploadées, utilisation des IDs serveur');
      
      // Préparation des IDs d'images
      let serverImageIds = [];
      
      // Ajouter les IDs serveur si disponibles
      if (anomalieServerId.value) serverImageIds.push(anomalieServerId.value);
      
      console.log('Envoi direct avec les IDs serveur:', serverImageIds);
      
      // Mise à jour directe en utilisant PATCH
      try {
        const updatePayload = {
          status: 'NOT DELIVERED',
          undone_reason: anomalyReason.value,
          image_ids: serverImageIds
        };
        
        await store.dispatch('logs/info', {
          message: `Mise à jour directe de la livraison ${props.delivery.id}`,
          data: { 
            status: 'NOT DELIVERED',
            undoneReason: anomalyReason.value,
            imageIds: serverImageIds
          }
        });
        
        // Appeler l'API directement
        const response = await axios.patch(`/livraison/delivery/${props.delivery.id}/`, updatePayload);
        
        console.log('Livraison mise à jour avec succès (direct):', response.data);
        
        // IMPORTANT: Mettre à jour également le state local pour refléter le changement
        // Mise à jour de la livraison dans la tournée
        store.commit('UPDATE_DELIVERY_IN_TOURNEE', {
          deliveryId: props.delivery.id,
          updates: {
            status: 'NOT DELIVERED',
            undone_reason: anomalyReason.value
          }
        });
        
        // Mise à jour du cache local
        await store.dispatch('sync/refreshPendingUpdates');
        
        // Recharger la livraison depuis le serveur pour être sûr d'avoir les données à jour
        if (isOnline.value) {
          try {
            const freshData = await axios.get(`/livraison/delivery/${props.delivery.id}/`);
            if (freshData && freshData.data) {
              // Mettre à jour la livraison dans la tournée avec les données fraîches
              store.commit('UPDATE_DELIVERY_IN_TOURNEE', {
                deliveryId: props.delivery.id,
                updates: freshData.data
              });
              
              // Mettre à jour la livraison dans le dataService
              await dataService.saveDelivery(props.delivery.id, freshData.data);
              
              // Émettre un événement pour informer les autres composants de la mise à jour
              if (eventBus) {
                eventBus.deliveryUpdated(props.delivery.id);
              }
            }
          } catch (refreshError) {
            console.warn('Impossible de recharger les données fraîches:', refreshError);
            // Ne pas bloquer le processus si la récupération échoue
          }
        }
        
        emit('success', {
          status: 'NOT DELIVERED',
          undone_reason: anomalyReason.value
        });
        
        return {
          success: true,
          status: 'NOT DELIVERED',
          undoneReason: anomalyReason.value
        };
      } catch (error) {
        console.error('Erreur lors de la mise à jour directe:', error);
        // En cas d'erreur, on retombe sur le mécanisme standard
      }
    }
    
    // Si nous sommes hors ligne ou que les images n'ont pas pu être uploadées directement,
    // on utilise le mécanisme standard avec les données locales
    console.log('Utilisation du mécanisme de mise à jour standard');
    
    // Préparation des images à envoyer
    let allImages = []
    
    // Ajouter l'image d'anomalie si disponible
    if (anomalieImageData.value) {
      allImages.push({
        id: anomalieImageData.value.id,
        file: anomalieImageData.value.file,
        context: 'anomalie',
        // Si l'image a déjà été uploadée, inclure l'ID serveur
        serverImageId: anomalieServerId.value
      })
    }
    
    console.log('Envoi des données pour signalement d\'anomalie...')

    // Mise à jour du statut de livraison via le store
    const result = await store.dispatch('updateDeliveryStatus', {
      deliveryId: props.delivery.id,
      status: 'NOT DELIVERED',
      undoneReason: anomalyReason.value,
      images: allImages,
      waitForSync: false // On ne force pas l'attente car les images sont déjà en cours d'upload
    })

    if (result.success) {
      console.log('Anomalie signalée avec succès')
      
      // S'assurer que l'interface est mise à jour, même en mode hors ligne
      // Dans tous les cas mettre à jour la livraison dans le cache local
      try {
        // Lire la livraison actuelle du cache (ce qui inclut déjà les modifications)
        const updatedDelivery = await dataService.getDelivery(props.delivery.id);
        
        // Mettre à jour le cache local
        if (updatedDelivery) {
          await dataService.saveDelivery(props.delivery.id, {
            ...updatedDelivery,
            status: 'NOT DELIVERED',
            undone_reason: anomalyReason.value
          });
          
          // Émettre un événement pour informer les autres composants de la mise à jour
          if (eventBus) {
            eventBus.deliveryUpdated(props.delivery.id);
          }
        }
      } catch (err) {
        // Ne pas bloquer si cette opération échoue
        console.warn('Erreur lors de la mise à jour du cache local:', err);
      }
      
      emit('success', {
        status: 'NOT DELIVERED',
        undone_reason: anomalyReason.value
      })
      
      return {
        success: true,
        status: 'NOT DELIVERED',
        undoneReason: anomalyReason.value
      };
    }
  } catch (error) {
    console.error('Error in handleConfirm:', error)
  }

  return {
    success: false
  };
}

// Expose les propriétés et méthodes nécessaires pour le composant parent
defineExpose({
  isFormValid,
  handleConfirm
})
</script>

<template>
  <div class="anomalie-modal-content" v-if="delivery">
    <div class="anomalie-form">
      <!-- Status Checks Section -->
      <div class="checks-container">
        <div class="check-item">
          <label>Client appelé ?</label>
          <div class="button-group">
            <Button :class="{
              'active': clientCalled === true,
              'yes-button': true,
              'yes-selected': clientCalled === true
            }" severity="success" text raised @click="clientCalled = true" icon="pi pi-check" label="Oui" />
            <Button :class="{
              'active': clientCalled === false,
              'no-button': true,
              'no-selected': clientCalled === false
            }" severity="danger" text raised @click="clientCalled = false" icon="pi pi-times" label="Non" />
          </div>
          <div v-if="clientCalled === false" class="call-suggestion">
            <p>Veuillez appeler le client avant de continuer</p>
            <Button severity="danger" @click="callClient" icon="pi pi-phone" label="Appeler le client" />
          </div>
        </div>

        <div class="check-item">
          <label>Astreinte appelé ?</label>
          <div class="button-group">
            <Button :class="{
              'active': operationsCalled === true,
              'yes-button': true,
              'yes-selected': operationsCalled === true
            }" severity="success" text raised @click="operationsCalled = true" icon="pi pi-check" label="Oui" />
            <Button :class="{
              'active': operationsCalled === false,
              'no-button': true,
              'no-selected': operationsCalled === false
            }" severity="danger" text raised @click="operationsCalled = false" icon="pi pi-times" label="Non" />
          </div>
          <div v-if="operationsCalled === false" class="call-suggestion">
            <p>Veuillez appeler l'astreinte avant de continuer</p>
            <Button severity="danger" @click="callSupport" icon="pi pi-phone" label="Appeler l'astreinte" />
          </div>
        </div>

        <div class="check-item">
          <label>Raison de l'anomalie</label>
          <Dropdown v-model="anomalyReason" :options="anomalyReasons" placeholder="Sélectionnez une raison"
            class="anomaly-dropdown" />
        </div>
      </div>

      <!-- Section Upload Photo Anomalie -->
      <div class="upload-section">
        <div class="section-header">
          <h4>Photo de l'anomalie</h4>
          <small>Prendre une photo pour justifier l'anomalie</small>
        </div>
        <ProgressiveImageUploader 
          ref="anomalieUploader"
          :delivery-id="delivery.id" 
          delivery-context="anomalie"
          @success="handleAnomalieSuccess" 
          @error="handleUploadError"
          @upload-complete="handleAnomalieUploadComplete"
        />
      </div>
      
      <!-- Indicateur de statut d'upload -->
      <div v-if="isOnline && anomalieUploaded" class="upload-status-indicator">
        <div class="status-header">
          <i :class="allUploadsComplete ? 'pi pi-check-circle status-icon complete' : 'pi pi-sync status-icon uploading'" 
             :style="!allUploadsComplete ? { animation: 'spin 2s linear infinite' } : {}">
          </i>
          <span class="status-text">{{ allUploadsComplete ? 'Toutes les photos sont synchronisées' : 'Synchronisation des photos en cours...' }}</span>
        </div>
        <div class="status-details" v-if="!allUploadsComplete">
          <div v-if="anomalieUploaded" class="status-item" :class="{ complete: anomalieServerId }">
            <i :class="anomalieServerId ? 'pi pi-check-circle' : 'pi pi-clock'"></i>
            <span>Photo de l'anomalie</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="error-message">
    Données de livraison non disponibles
  </div>
</template>

<style scoped>
/* Layout principal */
.anomalie-modal-content {
  padding: 0.5rem;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.anomalie-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

/* Section des vérifications */
.checks-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 6px;
}

.check-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.check-item label {
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.75rem;
}

/* Groupe de boutons */
.button-group {
  display: flex;
  gap: 0.5rem;
}

.button-group .p-button {
  flex: 1;
  transition: background-color 0.2s ease;
}

/* États des boutons */
.button-group .p-button.yes-selected {
  background-color: rgba(87, 180, 87, 0.2) !important;
}

.button-group .p-button.no-selected {
  background-color: rgba(200, 50, 50, 0.2) !important;
}

.button-group .p-button.yes-selected:hover {
  background-color: rgba(87, 180, 87, 0.3) !important;
}

.button-group .p-button.no-selected:hover {
  background-color: rgba(200, 50, 50, 0.3) !important;
}

/* Menu déroulant */
.anomaly-dropdown {
  width: 100%;
}

/* Nouveau conteneur pour l'en-tête des sections */
.section-header {
  margin-bottom: 0.35rem;
}

.upload-section {
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.upload-section h4 {
  margin: 0;
  font-size: 0.75rem;
  color: #2c3e50;
  font-weight: 700;
  line-height: 1.2;
}

.upload-section small {
  display: block;
  font-size: 0.65rem;
  color: #6c757d;
  margin-top: 0.2rem;
  line-height: 1.2;
  /* Permettre le retour à la ligne pour les textes longs */
  white-space: normal;
  word-wrap: break-word;
}

.call-suggestion {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #ffe0e0;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.call-suggestion p {
  margin: 0;
  color: #ed0202;
  font-size: 0.875rem;
}

.error-message {
  padding: 1rem;
  text-align: center;
  color: #ef4444;
  font-weight: 500;
}

/* Style pour l'indicateur de statut d'upload */
.upload-status-indicator {
  margin-top: 0.5rem;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.status-icon {
  font-size: 1.1rem;
}

.status-icon.complete {
  color: #10b981;
}

.status-icon.uploading {
  color: #3b82f6;
}

.status-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #4b5563;
}

.status-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.status-item i {
  color: #f59e0b;
}

.status-item.complete i {
  color: #10b981;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Ajout de style global pour la modale PrimeVue */
:deep(.p-dialog) {
  max-width: 98vw !important;
  width: 98vw !important;
  margin: 0 auto;
}

:deep(.p-dialog-content) {
  padding: 0 0.5rem 1rem 0.5rem !important;
}

:deep(.p-dialog-header) {
  padding: 1rem 1rem !important;
}

:deep(.p-dialog-footer) {
  padding: 0 0.5rem 0.5rem 0.5rem !important;
}

/* Media queries pour les différentes tailles d'écran */
@media (min-width: 400px) {
  .anomalie-modal-content {
    padding: 0.75rem;
  }
  
  .upload-section h4 {
    font-size: 0.8rem;
  }
  
  .upload-section small {
    font-size: 0.7rem;
  }
  
  .upload-section {
    padding: 0.65rem;
    gap: 0.5rem;
  }
  
  :deep(.p-dialog-content) {
    padding: 0 0.75rem 1.5rem 0.75rem !important;
  }
}

@media (min-width: 640px) {
  .anomalie-modal-content {
    padding: 1rem;
  }
  
  .upload-section {
    padding: 0.85rem;
    gap: 0.75rem;
  }
  
  .upload-section h4 {
    font-size: 0.85rem;
  }
  
  .upload-section small {
    font-size: 0.75rem;
  }
  
  .check-item label {
    font-size: 0.875rem;
  }
  
  :deep(.p-dialog) {
    max-width: 95vw !important;
    width: 95vw !important;
  }
  
  :deep(.p-dialog-content) {
    padding: 0 1rem 1.5rem 1rem !important;
  }
  
  :deep(.p-dialog-header) {
    padding: 1.25rem 1.25rem !important;
  }
  
  :deep(.p-dialog-footer) {
    padding: 0 1rem 1rem 1rem !important;
  }
}

@media (min-width: 768px) {
  :deep(.p-dialog) {
    max-width: 90vw !important;
    width: 90vw !important;
  }
  
  .check-item {
    flex-direction: row;
    align-items: center;
  }
  
  .check-item label {
    min-width: 150px;
  }
  
  .button-group, .anomaly-dropdown {
    width: 250px;
  }
}

@media (min-width: 992px) {
  :deep(.p-dialog) {
    max-width: 80vw !important;
    width: 80vw !important;
  }
}
</style>